import { useMemo } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Info, Link2, Unlink2 } from "lucide-react";
import type { AutoGrantedPermission } from "@/services/api";
import type { Feature } from "@/types";

export interface DependencyWarningDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode: "enable" | "disable" | "block";
	featureName: string;
	// For enable mode - dependencies that will be auto-granted
	autoGrantedDependencies?: AutoGrantedPermission[];
	// For disable/block mode - features that depend on this one
	affectedDependents?: Array<{
		feature: string;
		featureName: string;
		reason?: string;
	}>;
	// Callbacks
	onConfirm: () => void;
	onCancel: () => void;
	// Optional: for enable mode, option to enable only the feature without dependencies
	allowPartialEnable?: boolean;
	onPartialEnable?: () => void;
	// Loading state
	isLoading?: boolean;
}

export function DependencyWarningDialog({
	open,
	onOpenChange,
	mode,
	featureName,
	autoGrantedDependencies = [],
	affectedDependents = [],
	onConfirm,
	onCancel,
	allowPartialEnable = false,
	onPartialEnable,
	isLoading = false,
}: DependencyWarningDialogProps) {
	const title = useMemo(() => {
		switch (mode) {
			case "enable":
				return "Auto-Grant Dependencies";
			case "disable":
				return "Warning: Dependent Features";
			case "block":
				return "Cannot Block Feature";
			default:
				return "Dependency Warning";
		}
	}, [mode]);

	const description = useMemo(() => {
		switch (mode) {
			case "enable":
				return `Enabling "${featureName}" requires the following dependencies to also be enabled:`;
			case "disable":
				return `Disabling "${featureName}" will affect the following features that depend on it:`;
			case "block":
				return `You cannot block "${featureName}" because the following enabled features depend on it:`;
			default:
				return "";
		}
	}, [mode, featureName]);

	const icon = useMemo(() => {
		switch (mode) {
			case "enable":
				return <Info className="w-5 h-5 text-blue-500" />;
			case "disable":
				return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
			case "block":
				return <AlertTriangle className="w-5 h-5 text-red-500" />;
			default:
				return <Info className="w-5 h-5" />;
		}
	}, [mode]);

	const confirmButtonVariant = useMemo(() => {
		switch (mode) {
			case "enable":
				return "default";
			case "disable":
				return "destructive";
			case "block":
				return "destructive";
			default:
				return "default";
		}
	}, [mode]);

	const confirmButtonText = useMemo(() => {
		switch (mode) {
			case "enable":
				return "Enable All";
			case "disable":
				return "Disable Anyway";
			case "block":
				return "OK";
			default:
				return "Confirm";
		}
	}, [mode]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{icon}
						{title}
					</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				<div className="py-4">
					{/* Enable mode - show dependencies to be auto-granted */}
					{mode === "enable" && autoGrantedDependencies.length > 0 && (
						<div className="space-y-2">
							{autoGrantedDependencies.map((dep, index) => (
								<div
									key={`${dep.feature}-${dep.action}-${index}`}
									className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
								>
									<Link2 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2">
											<span className="font-medium text-sm">
												{dep.feature}
											</span>
											<Badge variant="secondary" className="text-xs">
												{dep.action}
											</Badge>
										</div>
										{dep.reason && (
											<p className="text-xs text-muted-foreground mt-1">
												{dep.reason}
											</p>
										)}
									</div>
									<CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
								</div>
							))}
							<p className="text-xs text-muted-foreground mt-3">
								These permissions will be automatically enabled to ensure proper
								functionality.
							</p>
						</div>
					)}

					{/* Disable mode - show affected dependents */}
					{mode === "disable" && affectedDependents.length > 0 && (
						<div className="space-y-2">
							{affectedDependents.map((dep, index) => (
								<div
									key={`${dep.feature}-${index}`}
									className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100"
								>
									<Unlink2 className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
									<div className="flex-1 min-w-0">
										<span className="font-medium text-sm">
											{dep.featureName || dep.feature}
										</span>
										{dep.reason && (
											<p className="text-xs text-muted-foreground mt-1">
												Requires: {dep.reason}
											</p>
										)}
									</div>
									<AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
								</div>
							))}
							<p className="text-xs text-muted-foreground mt-3">
								These features may not work correctly if you proceed.
							</p>
						</div>
					)}

					{/* Block mode - show why blocking is not allowed */}
					{mode === "block" && affectedDependents.length > 0 && (
						<div className="space-y-2">
							{affectedDependents.map((dep, index) => (
								<div
									key={`${dep.feature}-${index}`}
									className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100"
								>
									<Unlink2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
									<div className="flex-1 min-w-0">
										<span className="font-medium text-sm">
											{dep.featureName || dep.feature}
										</span>
										{dep.reason && (
											<p className="text-xs text-muted-foreground mt-1">
												{dep.reason}
											</p>
										)}
									</div>
								</div>
							))}
							<p className="text-xs text-muted-foreground mt-3">
								You must first remove or block these features before blocking "
								{featureName}".
							</p>
						</div>
					)}

					{/* No dependencies case */}
					{mode === "enable" && autoGrantedDependencies.length === 0 && (
						<p className="text-sm text-muted-foreground">
							No additional dependencies required.
						</p>
					)}
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					{mode !== "block" && (
						<Button variant="outline" onClick={onCancel} disabled={isLoading}>
							Cancel
						</Button>
					)}
					{mode === "enable" && allowPartialEnable && onPartialEnable && (
						<Button
							variant="ghost"
							onClick={onPartialEnable}
							disabled={isLoading}
						>
							Enable Only This
						</Button>
					)}
					<Button
						variant={confirmButtonVariant as any}
						onClick={mode === "block" ? onCancel : onConfirm}
						disabled={isLoading}
					>
						{isLoading ? "Processing..." : confirmButtonText}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ==========================================
// Helper hook for dependency management
// ==========================================

export interface UseDependencyWarningOptions {
	features: Feature[];
	dependencyMap: Record<string, Array<{ feature: string; action: string; reason?: string }>>;
	reverseDependencyMap: Record<string, string[]>;
	enabledFeatures: string[];
}

export interface DependencyCheckResult {
	hasDependencies: boolean;
	dependencies: Array<{ feature: string; action: string; reason?: string }>;
	hasDependents: boolean;
	dependents: Array<{ feature: string; featureName: string; reason?: string }>;
	enabledDependents: Array<{ feature: string; featureName: string; reason?: string }>;
}

export function useDependencyCheck(options: UseDependencyWarningOptions) {
	const { features, dependencyMap, reverseDependencyMap, enabledFeatures } = options;

	const getFeatureName = (code: string): string => {
		const feature = features.find(
			(f) => f.slug === code || f.code === code,
		);
		return feature?.ui?.label || feature?.name || code;
	};

	const checkFeatureDependencies = (featureCode: string): DependencyCheckResult => {
		const code = featureCode.toLowerCase();
		const dependencies = dependencyMap[code] || [];
		const dependentCodes = reverseDependencyMap[code] || [];

		const dependents = dependentCodes.map((depCode) => {
			// Find the reason from the dependent's requires
			const depFeatureDeps = dependencyMap[depCode] || [];
			const reasonDep = depFeatureDeps.find(
				(d) => d.feature.toLowerCase() === code,
			);
			return {
				feature: depCode,
				featureName: getFeatureName(depCode),
				reason: reasonDep?.reason,
			};
		});

		const enabledDependents = dependents.filter((dep) =>
			enabledFeatures.some(
				(f) => f.toLowerCase() === dep.feature.toLowerCase(),
			),
		);

		return {
			hasDependencies: dependencies.length > 0,
			dependencies,
			hasDependents: dependents.length > 0,
			dependents,
			enabledDependents,
		};
	};

	const getMissingDependencies = (
		featureCode: string,
	): Array<{ feature: string; action: string; reason?: string }> => {
		const code = featureCode.toLowerCase();
		const dependencies = dependencyMap[code] || [];

		return dependencies.filter(
			(dep) =>
				!enabledFeatures.some(
					(f) => f.toLowerCase() === dep.feature.toLowerCase(),
				),
		);
	};

	return {
		checkFeatureDependencies,
		getMissingDependencies,
		getFeatureName,
	};
}
